/**
 * GateDetailDrawer — the full read view of one checklist/gate, either a Work
 * Library template (before attaching) or an attached instance. Shows everything
 * the site engineer will actually face: agenda topics, briefing, check items,
 * hazards / PPE / precautions, acceptance criteria — so the DM can judge a
 * checklist before putting it on an activity.
 */
import { Drawer } from 'antd';
import { Camera, Check, Plus, X } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { SOVEREIGN } from '../../../../utils/epcProjectVisual';
import type { CheckItem, EducationGate, QaqcGate, SafetyGate } from '../../../types';

export type GateViewModel =
  | { kind: 'education'; gate: EducationGate }
  | { kind: 'safety'; gate: SafetyGate }
  | { kind: 'qaqc'; gate: QaqcGate };

const KIND_META: Record<GateViewModel['kind'], { label: string; chip: CSSProperties }> = {
  education: { label: 'Education', chip: { background: 'rgba(51,146,116,0.12)', color: '#1F5A47' } },
  safety: { label: 'Safety permit', chip: { background: 'rgba(201,169,97,0.18)', color: '#8A5A08' } },
  qaqc: { label: 'QA/QC inspection', chip: { background: 'rgba(11,59,46,0.10)', color: '#0B3B2E' } },
};

const EDU_TYPE_LABELS: Record<EducationGate['eduType'], string> = {
  tbt: 'Toolbox talk (TBT)',
  method_statement: 'Method statement',
  induction: 'Induction',
};

const QAQC_BEHAVIOR_EXPLAIN: Record<QaqcGate['behavior'], string> = {
  hold: 'Hold point — work must stop at this gate until an approver passes the inspection.',
  witness: 'Witness point — the approver should observe; work may continue if they are unavailable.',
  self: 'Self-certification — the site engineer confirms the criteria themselves.',
};

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div
      className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.12em]"
      style={{ color: SOVEREIGN.kpiCaption }}
    >
      {children}
    </div>
  );
}

function ItemList({ items }: { items: CheckItem[] }) {
  if (items.length === 0) {
    return <p className="text-[11.5px] italic" style={{ color: '#94A3B8' }}>None defined.</p>;
  }
  return (
    <ul className="flex flex-col gap-1">
      {items.map((c) => (
        <li
          key={c.id}
          className="flex items-start gap-2 rounded-lg px-2.5 py-1.5 text-[12px]"
          style={{ background: '#F8F2E2', color: SOVEREIGN.title }}
        >
          <span
            className="mt-0.5 h-3 w-3 shrink-0 rounded-[3px] border"
            style={{ borderColor: 'rgba(11,59,46,0.35)', background: '#fff' }}
            aria-hidden
          />
          <span className="min-w-0 flex-1">{c.label}</span>
          {c.required ? (
            <span
              className="shrink-0 rounded-full px-1.5 py-px text-[9.5px] font-semibold uppercase"
              style={{ background: 'rgba(11,59,46,0.08)', color: '#1F5A47' }}
            >
              required
            </span>
          ) : null}
          {c.photoRequired ? (
            <span
              className="flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-px text-[9.5px] font-semibold uppercase"
              style={{ background: 'rgba(201,169,97,0.18)', color: '#8A5A08' }}
              title="Photo evidence required"
            >
              <Camera size={9} aria-hidden /> photo
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function EducationBody({ gate }: { gate: EducationGate }) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <SectionLabel>Type</SectionLabel>
        <p className="text-[12.5px] font-semibold" style={{ color: SOVEREIGN.title }}>
          {EDU_TYPE_LABELS[gate.eduType]}
        </p>
      </div>
      <div>
        <SectionLabel>Agenda topics</SectionLabel>
        {gate.agendaTopics.length ? (
          <ul className="flex flex-col gap-0.5 text-[12px]" style={{ color: SOVEREIGN.title }}>
            {gate.agendaTopics.map((t) => (
              <li key={t} className="flex items-baseline gap-1.5">
                <span style={{ color: '#C9A961' }}>•</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[11.5px] italic" style={{ color: '#94A3B8' }}>No agenda topics.</p>
        )}
      </div>
      {gate.briefing.trim() ? (
        <div>
          <SectionLabel>Briefing</SectionLabel>
          <p
            className="whitespace-pre-wrap rounded-lg px-3 py-2 text-[12px] leading-relaxed"
            style={{ background: '#F8F2E2', color: SOVEREIGN.title }}
          >
            {gate.briefing}
          </p>
        </div>
      ) : null}
      <div>
        <SectionLabel>Check items — the engineer ticks these before starting</SectionLabel>
        <ItemList items={gate.checkItems} />
      </div>
    </div>
  );
}

function SafetyBody({ gate }: { gate: SafetyGate }) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <SectionLabel>Permit type</SectionLabel>
        <p className="text-[12.5px] font-semibold capitalize" style={{ color: SOVEREIGN.title }}>
          {gate.permitType} — permit to work
        </p>
      </div>
      <div>
        <SectionLabel>Hazards</SectionLabel>
        <ItemList items={gate.hazards} />
      </div>
      <div>
        <SectionLabel>PPE</SectionLabel>
        <ItemList items={gate.ppe} />
      </div>
      <div>
        <SectionLabel>Precautions</SectionLabel>
        <ItemList items={gate.precautions} />
      </div>
    </div>
  );
}

function QaqcBody({ gate }: { gate: QaqcGate }) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <SectionLabel>Behaviour</SectionLabel>
        <p className="text-[12.5px] font-semibold capitalize" style={{ color: SOVEREIGN.title }}>
          {gate.behavior}
        </p>
        <p className="mt-1 text-[11.5px] leading-relaxed" style={{ color: SOVEREIGN.kpiCaption }}>
          {QAQC_BEHAVIOR_EXPLAIN[gate.behavior]}
        </p>
      </div>
      <div>
        <SectionLabel>Acceptance criteria</SectionLabel>
        <ItemList items={gate.acceptanceCriteria} />
      </div>
    </div>
  );
}

export interface GateDetailDrawerProps {
  view: GateViewModel | null;
  /** Whether this checklist is currently attached to the activity. */
  attached: boolean;
  onClose: () => void;
  /** Attach/detach action; omit to render a read-only view. */
  onToggleAttach?: () => void;
}

export function GateDetailDrawer({ view, attached, onClose, onToggleAttach }: GateDetailDrawerProps) {
  const meta = view ? KIND_META[view.kind] : null;
  return (
    <Drawer
      open={!!view}
      onClose={onClose}
      width={430}
      closable={false}
      styles={{ body: { background: '#FDFAF3', padding: 0 } }}
      title={null}
    >
      {view && meta ? (
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="border-b px-5 py-4" style={{ borderColor: 'rgba(11,59,46,0.10)' }}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <span
                  className="inline-block rounded-full px-2 py-px text-[10px] font-bold uppercase tracking-wide"
                  style={meta.chip}
                >
                  {meta.label}
                </span>
                <h3 className="mt-1.5 text-[15px] font-extrabold leading-snug" style={{ color: '#0B3B2E' }}>
                  {view.gate.title}
                </h3>
                {attached ? (
                  <p className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold" style={{ color: '#1F5A47' }}>
                    <Check size={12} aria-hidden /> attached to this activity
                  </p>
                ) : (
                  <p className="mt-0.5 text-[11px]" style={{ color: SOVEREIGN.kpiCaption }}>
                    not attached yet
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close details"
                className="shrink-0 rounded-md p-1 transition-colors hover:bg-[rgba(11,59,46,0.06)]"
                style={{ color: SOVEREIGN.kpiCaption }}
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            {view.kind === 'education' ? <EducationBody gate={view.gate} /> : null}
            {view.kind === 'safety' ? <SafetyBody gate={view.gate} /> : null}
            {view.kind === 'qaqc' ? <QaqcBody gate={view.gate} /> : null}
          </div>

          {/* Footer action */}
          {onToggleAttach ? (
            <div className="border-t px-5 py-3" style={{ borderColor: 'rgba(11,59,46,0.10)' }}>
              <button
                type="button"
                onClick={onToggleAttach}
                className="flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-[12.5px] font-semibold transition-colors"
                style={
                  attached
                    ? { background: 'rgba(159,18,57,0.08)', color: '#9F1239', border: '1px solid rgba(159,18,57,0.25)' }
                    : { background: SOVEREIGN.title, color: '#F8F2E2' }
                }
              >
                {attached ? (
                  <>
                    <X size={13} aria-hidden /> Remove from this activity
                  </>
                ) : (
                  <>
                    <Plus size={13} aria-hidden /> Add to this activity
                  </>
                )}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Drawer>
  );
}
